import React from 'react'
import type { Table } from '@tanstack/table-core'
import styled from 'styled-components'

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  value: string
  searchInput: string | null
  setSearchInput: React.Dispatch<React.SetStateAction<string | null>>
  setSearchQuery: React.Dispatch<React.SetStateAction<string | null>>
  tableInstance: Table<any>
  isLoading: boolean
  placeholder?: string
}

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  position: relative;
`

const StyledInput = styled.input`
  background-color: transparent;
  color: #85f6f4;
  padding: 6px 10px;
  border: 1px solid #85f6f4;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 400;
  width: 100%;
  &::placeholder {
    color: white;
    opacity: 0.5;
    font-style: italic;
  }
  &:focus {
    outline: none;
    box-shadow: none;
    border: 1px solid #85f6f4;
  }
`

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.primaryColor300};
  padding: 0;
  line-height: 1;
  z-index: 2;
`

const SearchInput: React.FC<SearchInputProps> = ({
  label,
  searchInput,
  setSearchInput,
  setSearchQuery,
  tableInstance,
  placeholder = '',
  isLoading,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && e.currentTarget.value.trim() !== '' && !isLoading) {
      tableInstance.resetPageIndex()
      setSearchQuery(searchInput)
    }
  }

  const handleOnclear = () => {
    tableInstance.resetPageIndex()
    setSearchInput(null)
    setSearchQuery(null)
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
    if (e.target.value.trim() === '') {
      tableInstance.resetPageIndex()
      setSearchQuery(null)
    }
  }

  return (
    <InputWrapper>
      {label && <label style={{ marginRight: 8 }}>{label}</label>}
      <StyledInput
        type="text"
        value={searchInput || ''}
        onChange={handleOnChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
      />
      {searchInput && (
        <ClearButton type="button" aria-label="Effacer" onClick={handleOnclear} tabIndex={0}>
          &#10006;
        </ClearButton>
      )}
    </InputWrapper>
  )
}

export default SearchInput
